import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../../config/axios';

export interface ABOFile {
  id: number;
  filename: string;
  bankType: string;
  status: string;
  totalAmount: number;
  invoiceCount: number;
  createdAt: string;
  content?: string;
  transactions?: any[];
  invoices?: any[];
}

interface ABOState {
  aboFiles: ABOFile[];
  currentABOFile: ABOFile | null;
  loading: boolean;
  error: string | null;
}

const initialState: ABOState = {
  aboFiles: [],
  currentABOFile: null,
  loading: false,
  error: null,
};

// Async thunks
export const fetchABOFiles = createAsyncThunk(
  'abo/fetchABOFiles',
  async (params: { businessUnitId?: string } = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      if (params?.businessUnitId) queryParams.append('businessUnitId', params.businessUnitId);
      
      const response = await api.get(`/abo?${queryParams.toString()}`);
      return response.data.aboFiles;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch ABO files');
    }
  }
);

export const fetchABOFileById = createAsyncThunk(
  'abo/fetchABOFileById',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await api.get(`/abo/${id}`);
      return response.data.aboFile;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch ABO file');
    }
  }
);

export const generateABOFile = createAsyncThunk(
  'abo/generateABOFile',
  async (data: { invoiceIds: number[]; bankType: string; businessUnitId: number }, { rejectWithValue }) => {
    try {
      const response = await api.post('/abo/generate', data);
      return response.data.aboFile;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to generate ABO file');
    }
  }
);

export const generateAndSubmitABOFile = createAsyncThunk(
  'abo/generateAndSubmitABOFile',
  async (data: { 
    invoiceIds: number[]; 
    bankType: string; 
    businessUnitId: number;
    submitNow: boolean;
    bankAccountId: number;
  }, { rejectWithValue }) => {
    try {
      const response = await api.post('/abo/generate', data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to generate and submit ABO file');
    }
  }
);

export const getReadyInvoices = createAsyncThunk(
  'abo/getReadyInvoices',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/invoices?status=ready_for_payment');
      return response.data.invoices;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch ready invoices');
    }
  }
);

export const sendToFIOBank = createAsyncThunk(
  'abo/sendToFIOBank',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await api.post(`/abo/${id}/send-to-fio`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to send to FIO bank');
    }
  }
);

const aboSlice = createSlice({
  name: 'abo',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentABOFile: (state) => {
      state.currentABOFile = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch ABO Files
      .addCase(fetchABOFiles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchABOFiles.fulfilled, (state, action) => {
        state.loading = false;
        state.aboFiles = action.payload;
      })
      .addCase(fetchABOFiles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch ABO File by ID
      .addCase(fetchABOFileById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchABOFileById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentABOFile = action.payload;
      })
      .addCase(fetchABOFileById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Generate ABO File
      .addCase(generateABOFile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(generateABOFile.fulfilled, (state, action) => {
        state.loading = false;
        state.aboFiles.unshift(action.payload);
      })
      .addCase(generateABOFile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Send to FIO Bank
      .addCase(sendToFIOBank.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendToFIOBank.fulfilled, (state, action) => {
        state.loading = false;
        // Update the ABO file status in the list
        const index = state.aboFiles.findIndex(file => file.id === action.payload.id);
        if (index !== -1) {
          state.aboFiles[index].status = 'sent_to_bank';
        }
        // Update current ABO file if it's the same one
        if (state.currentABOFile?.id === action.payload.id) {
          state.currentABOFile!.status = 'sent_to_bank';
        }
      })
      .addCase(sendToFIOBank.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Generate and Submit ABO File
      .addCase(generateAndSubmitABOFile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(generateAndSubmitABOFile.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.aboFile) {
          state.aboFiles.unshift(action.payload.aboFile);
        }
      })
      .addCase(generateAndSubmitABOFile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearCurrentABOFile } = aboSlice.actions;
export default aboSlice.reducer; 